import { create } from 'zustand';

type ShapeType = 'circle' | 'triangle' | 'square';

interface DashboardState {
  progressWidth: number;
  currentPoints: number;
  remainingPoints: number;
  currentLevel: number;
  studentFirstName: string;
  displayPoints: number;
  displayRemainingPoints: number;
  selectedShape: ShapeType;
  setStudentFirstName: (name: string) => void;
  setSelectedShape: (shape: ShapeType) => void;
  addPoints: (points: number) => void;
  animateCounter: () => void;
  animateRemainingCounter: () => void;
  animateProgress: () => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  progressWidth: 0,
  currentPoints: 78,
  remainingPoints: 22,
  currentLevel: 5,
  studentFirstName: "",
  displayPoints: 0,
  displayRemainingPoints: 100,
  selectedShape: 'circle',

  setStudentFirstName: (name: string) => set({ studentFirstName: name }),
  setSelectedShape: (shape: ShapeType) => set({ selectedShape: shape }),

  addPoints: (points: number) => {
    const currentPoints = get().currentPoints;
    const newPoints = currentPoints + points;
    const remainingPoints = 100 - (newPoints % 100);
    const currentLevel = Math.floor(newPoints / 100) + 1;
    
    set({ 
      currentPoints: newPoints,
      remainingPoints,
      currentLevel,
      displayPoints: newPoints,
      displayRemainingPoints: remainingPoints
    });
  },

  animateCounter: () => {
    const duration = 1000;
    const startTime = Date.now();
    const startValue = 0;
    const endValue = get().currentPoints;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      set({ displayPoints: Math.round(startValue + (endValue - startValue) * progress) });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  },

  animateRemainingCounter: () => {
    const duration = 1000;
    const startTime = Date.now();
    const startValue = 100;
    const endValue = get().remainingPoints;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      set({ displayRemainingPoints: Math.round(startValue + (endValue - startValue) * progress) });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  },

  animateProgress: () => {
    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      set({ progressWidth: get().currentPoints * progress });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }
}));